import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../config/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useAdminCheck } from '../../hooks/useAdminCheck';
import LoadingSpinner from '../../components/LoadingSpinner';
import PageHeader from '../../components/PageHeader';

const GeoEvent = () => {
  const { isAdmin, loading: adminLoading } = useAdminCheck();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [selectedEvents, setSelectedEvents] = useState(new Set());
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    withCoords: 0,
    withoutCoords: 0,
    processed: 0,
    success: 0,
    failed: 0
  });

  useEffect(() => {
    if (user) {
      fetchEvents();
    }
  }, [user]);

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { message, type, timestamp }]);
  };

  const fetchEvents = async () => {
    setLoading(true);
    addLog('Chargement des événements...');

    try {
      const { data, error } = await supabase
        .from('events')
        .select('id, title, formatted_address, street_number, street_name, postal_code, city, region, country, latitude, longitude')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const withCoords = data?.filter(e => e.latitude && e.longitude).length || 0;
      const withoutCoords = data?.filter(e => !e.latitude || !e.longitude).length || 0;

      setEvents(data || []);
      setStats(prev => ({
        ...prev,
        total: data?.length || 0,
        withCoords,
        withoutCoords
      }));

      addLog(`${data?.length || 0} événements chargés (${withCoords} avec coordonnées, ${withoutCoords} sans)`, 'success');
    } catch (error) {
      addLog(`Erreur: ${error.message}`, 'error');
      console.error('Erreur fetch events:', error);
    } finally {
      setLoading(false);
    }
  };

  const geocodeEvent = async (event) => {
    // Ne rien faire si l'événement a déjà des coordonnées
    if (
      event.latitude !== null &&
      event.latitude !== undefined &&
      String(event.latitude).toString().trim() !== '' &&
      event.longitude !== null &&
      event.longitude !== undefined &&
      String(event.longitude).toString().trim() !== ''
    ) {
      addLog(`[${event.title}] Déjà géocodé — aucune action nécessaire`, 'info');
      return { success: true, skipped: true };
    }

    // Construire l'adresse pour Nominatim
    const addressParts = [
      event.street_number,
      event.street_name,
      event.formatted_address,
      event.postal_code,
      event.city,
      event.region,
      event.country
    ].filter(Boolean);

    const address = addressParts.join(', ');

    if (!address.trim()) {
      addLog(`[${event.title}] Aucune adresse disponible`, 'error');
      return { success: false, error: 'Aucune adresse' };
    }

    addLog(`[${event.title}] Géocodage: ${address}`);

    try {
      const nominatimUrl = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1&addressdetails=1`;
      
      const response = await fetch(nominatimUrl, {
        headers: {
          'Accept-Language': i18n.language,
          'User-Agent': 'FamilyEventsApp/1.0'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      if (!data || data.length === 0) {
        addLog(`[${event.title}] Aucun résultat trouvé`, 'error');
        return { success: false, error: 'Aucun résultat' };
      }

      const result = data[0];
      const latitude = parseFloat(result.lat);
      const longitude = parseFloat(result.lon);

      // Log des coordonnées obtenues
      addLog(`[${event.title}] Coordonnées reçues de Nominatim: lat=${latitude}, lon=${longitude}`, 'info');

      // Préparer les données pour Supabase
      const updateData = {
        latitude: latitude,
        longitude: longitude,
        updated_at: new Date().toISOString()
      };
      
      addLog(`[${event.title}] Envoi à Supabase (ID: ${event.id}, User: ${user?.id}): ${JSON.stringify(updateData)}`, 'info');

      // Mettre à jour l'événement dans la base de données avec l'authentification
      const { data: updateResult, error: updateError } = await supabase
        .from('events')
        .update(updateData)
        .eq('id', event.id)
        .select('id, latitude, longitude');

      addLog(`[${event.title}] Réponse Supabase: error=${updateError ? JSON.stringify(updateError) : 'null'}, data=${JSON.stringify(updateResult)}`, updateError ? 'error' : 'info');

      if (updateError) {
        throw updateError;
      }

      // Si pas d'erreur mais data vide, vérifier si l'événement existe
      if (!updateResult || updateResult.length === 0) {
        addLog(`[${event.title}] ⚠ Update réussi mais aucune donnée retournée - vérification...`, 'warning');
        
        // Vérifier si l'événement existe avec une lecture directe
        const { data: checkData, error: checkError } = await supabase
          .from('events')
          .select('id, latitude, longitude')
          .eq('id', event.id)
          .single();
        
        addLog(`[${event.title}] Vérification: error=${checkError ? checkError.message : 'null'}, data=${JSON.stringify(checkData)}`, checkError ? 'error' : 'info');
        
        if (checkError) {
          addLog(`[${event.title}] ✗ Événement introuvable après update: ${checkError.message}`, 'error');
          return { success: false, error: 'Événement introuvable ou permissions insuffisantes' };
        }
        
        if (checkData && checkData.latitude && checkData.longitude) {
          addLog(`[${event.title}] ✓ Vérification OK - coordonnées bien enregistrées: ${checkData.latitude}, ${checkData.longitude}`, 'success');
          return { success: true, latitude: checkData.latitude, longitude: checkData.longitude };
        } else {
          addLog(`[${event.title}] ✗ Les coordonnées n'ont pas été enregistrées`, 'error');
          return { success: false, error: 'Coordonnées non enregistrées' };
        }
      }

      addLog(`[${event.title}] ✓ Coordonnées mises à jour: ${latitude}, ${longitude}`, 'success');
      return { success: true, latitude, longitude };

    } catch (error) {
      addLog(`[${event.title}] ✗ Erreur: ${error.message}`, 'error');
      console.error(`[${event.title}] Stack trace:`, error);
      return { success: false, error: error.message };
    }
  };

  const handleGeocodeSelected = async () => {
    if (selectedEvents.size === 0) {
      alert('Veuillez sélectionner au moins un événement');
      return;
    }

    setProcessing(true);
    let successCount = 0;
    let failedCount = 0;
    
    setStats(prev => ({ ...prev, processed: 0, success: 0, failed: 0 }));
    addLog(`Début du géocodage de ${selectedEvents.size} événements...`, 'info');

    const eventsToGeocode = events.filter(e => selectedEvents.has(e.id));
    
    for (let i = 0; i < eventsToGeocode.length; i++) {
      const event = eventsToGeocode[i];
      
      const result = await geocodeEvent(event);
      
      if (result.success && !result.skipped) {
        successCount++;
      } else if (!result.success) {
        failedCount++;
      }
      
      setStats(prev => ({
        ...prev,
        processed: i + 1,
        success: successCount,
        failed: failedCount
      }));

      // Respecter les limites de l'API Nominatim (1 req/sec)
      if (i < eventsToGeocode.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 1100));
      }
    }

    addLog(`Géocodage terminé: ${successCount} succès, ${failedCount} échecs`, 'info');
    setProcessing(false);
    
    // Recharger les événements
    await fetchEvents();
    setSelectedEvents(new Set());
  };

  const handleSelectAll = () => {
    if (selectedEvents.size === events.length) {
      setSelectedEvents(new Set());
    } else {
      setSelectedEvents(new Set(events.map(e => e.id)));
    }
  };

  const handleSelectWithoutCoords = () => {
    const withoutCoords = events.filter(e => !e.latitude || !e.longitude);
    setSelectedEvents(new Set(withoutCoords.map(e => e.id)));
  };

  const toggleEventSelection = (eventId) => {
    const newSelection = new Set(selectedEvents);
    if (newSelection.has(eventId)) {
      newSelection.delete(eventId);
    } else {
      newSelection.add(eventId);
    }
    setSelectedEvents(newSelection);
  };

  if (adminLoading || loading) {
    return <LoadingSpinner fullScreen />;
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <PageHeader title="Géocodage des événements" backPath="/settings" />

      <div className="p-4">
        {/* Statistiques */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-sm text-gray-500">Total</div>
            <div className="text-2xl font-bold">{stats.total}</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg shadow">
            <div className="text-sm text-green-600">Avec coordonnées</div>
            <div className="text-2xl font-bold text-green-600">{stats.withCoords}</div>
          </div>
          <div className="bg-orange-50 p-4 rounded-lg shadow">
            <div className="text-sm text-orange-600">Sans coordonnées</div>
            <div className="text-2xl font-bold text-orange-600">{stats.withoutCoords}</div>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <div className="text-sm text-blue-600">Sélectionnés</div>
            <div className="text-2xl font-bold text-blue-600">{selectedEvents.size}</div>
          </div>
        </div>

        {/* Actions */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleSelectAll}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg"
              disabled={processing || loading}
            >
              {selectedEvents.size === events.length ? 'Désélectionner tout' : 'Sélectionner tout'}
            </button>
            <button
              onClick={handleSelectWithoutCoords}
              className="px-4 py-2 bg-orange-200 hover:bg-orange-300 rounded-lg"
              disabled={processing || loading}
            >
              Sélectionner sans coordonnées
            </button>
            <button
              onClick={handleGeocodeSelected}
              className="px-4 py-2 bg-[#2894D8] hover:bg-[#136E9D] text-white rounded-lg"
              disabled={processing || loading || selectedEvents.size === 0}
            >
              {processing ? `Géocodage en cours (${stats.processed}/${selectedEvents.size})...` : `Géocoder ${selectedEvents.size} événement(s)`}
            </button>
            <button
              onClick={fetchEvents}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg ml-auto"
              disabled={processing || loading}
            >
              🔄 Actualiser
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Liste des événements */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b">
              <h2 className="text-h2">Événements ({events.length})</h2>
            </div>
            <div className="max-h-[600px] overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center text-gray-500">Chargement...</div>
              ) : events.length === 0 ? (
                <div className="p-4 text-center text-gray-500">Aucun événement</div>
              ) : (
                <div className="divide-y">
                  {events.map(event => (
                    <div
                      key={event.id}
                      className={`p-3 hover:bg-gray-50 cursor-pointer ${
                        selectedEvents.has(event.id) ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => toggleEventSelection(event.id)}
                    >
                      <div className="flex items-start gap-2">
                        <input
                          type="checkbox"
                          checked={selectedEvents.has(event.id)}
                          onChange={() => toggleEventSelection(event.id)}
                          className="mt-1"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">{event.title}</div>
                          <div className="text-xs text-gray-500 mt-1">
                            {event.formatted_address || 
                             [event.street_number, event.street_name].filter(Boolean).join(' ') ||
                             'Pas d\'adresse'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {event.postal_code} {event.city}
                          </div>
                          {event.latitude && event.longitude ? (
                            <div className="text-xs text-green-600 mt-1">
                              ✓ {event.latitude}, {event.longitude}
                            </div>
                          ) : (
                            <div className="text-xs text-orange-600 mt-1">
                              ⚠ Sans coordonnées
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Logs */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-h2">Logs ({logs.length})</h2>
              <button
                onClick={() => setLogs([])}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Effacer
              </button>
            </div>
            <div className="max-h-[600px] overflow-y-auto p-4 font-mono text-xs">
              {logs.length === 0 ? (
                <div className="text-center text-gray-500">Aucun log</div>
              ) : (
                <div className="space-y-1">
                  {logs.map((log, index) => (
                    <div
                      key={index}
                      className={`${
                        log.type === 'error' ? 'text-red-600' :
                        log.type === 'success' ? 'text-green-600' :
                        'text-gray-700'
                      }`}
                    >
                      <span className="text-gray-400">[{log.timestamp}]</span> {log.message}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeoEvent;
